import { Side } from '@floating-ui/react'
import { IconClockHour5 } from '@tabler/icons-react'
import { Dayjs } from 'dayjs'
import { ChangeEvent, FocusEventHandler, KeyboardEvent, MouseEventHandler, useId, useRef } from 'react'

import { BodyVariant, SizeOptions } from '../../../../types/types'
import { day } from '../../../../utils/extendedDayjs'
import { AstraDropdownMenu } from '../../../AstraDropdownMenu/AstraDropdownMenu'
import { AstraIcon } from '../../../AstraIcon/AstraIcon'
import { AstraParagraph } from '../../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../../Utilities/astraTwMerge'
import { AstraButton } from '../../AstraButton/AstraButton'
import { AstraInputWrapper, getHelperTextId, inputTypographyStyles } from '../../AstraInputWrapper/AstraInputWrapper'
import { AstraTimePickerSelectionMenu } from './AstraTimePickerSelectionMenu'
import { TimePickerProps } from './types'
import { calculateTimeSelection, prefixValue } from './utils'

const typographyStyles: Record<SizeOptions, BodyVariant> = {
    small: 'body-small',
    medium: 'body',
    large: 'body',
}

export const sizeStyles: Record<SizeOptions, string> = {
    small: 'h-5', // 20px, 1.25rem
    medium: 'h-7', // 28px, 1.75rem
    large: 'h-9', // 36px, 2.25rem
}

const timeInputStyles =
    'w-[2ch] bg-inherit text-end caret-transparent focus:outline-none active:outline-none data-[disabled]:cursor-not-allowed data-[disabled]:border-disabled data-[disabled]:text-disabled data-[placeholder]:text-subtle'

const onInputChange = (event: ChangeEvent<HTMLInputElement>, onLengthSetToMax: () => void, placeholder?: string) => {
    // Remove all non-digit characters and limit to 2 digits
    let value = event.target.value
    value = placeholder && value.startsWith(placeholder) ? value.slice(placeholder.length) : value
    const valueAsNumber = Math.abs(Number(value))

    if (isNaN(Number(valueAsNumber))) {
        return
    }

    if (value.length > 2) {
        onLengthSetToMax()
    }

    return valueAsNumber
}

export type AstraTimePickerProps = {
    helperText?: string
    errorText?: string
    error?: boolean
    label?: string
    fullWidth?: boolean
    size?: SizeOptions
    side?: Side
    onChange?: (value: Dayjs | undefined) => void
    value?: Dayjs
    allowClear?: boolean
    placeholder?: string
    disabled?: boolean
    readOnly?: boolean
    'data-test'?: string
} & TimePickerProps

/**
 * Primary UI component for time selection
 *
 * This component is a time input field that allows users to select time.
 */
export const AstraTimePicker = ({
    label,
    disabled,
    readOnly,
    onChange,
    value,
    minTime,
    maxTime,
    hourInterval = 1,
    minuteInterval = 1,
    size = 'small',
    side = 'bottom',
    placeholder = '--',
    'data-test': dataTest,
    allowClear = true,
    ...props
}: AstraTimePickerProps) => {
    const id = useId()
    const hourInputRef = useRef<HTMLInputElement>(null)
    const minuteInputRef = useRef<HTMLInputElement>(null)

    const timeValue = value?.clone()
    const timeSelection = calculateTimeSelection(hourInterval, minuteInterval)

    const clearTime = () => {
        if (allowClear) {
            onChange?.(undefined)
        }
    }

    const updateTime = (updates: Partial<{ hour: number; minute: number }>) => {
        let updated = timeValue ?? day()
        if (typeof updates.hour === 'number') updated = updated.set('hour', updates.hour)
        if (typeof updates.minute === 'number') updated = updated.set('minute', updates.minute)
        onChange?.(updated)
    }

    const onInputHourChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = onInputChange(event, () => minuteInputRef.current?.focus(), placeholder)

        value === undefined ? clearTime() : updateTime({ hour: value })
    }

    const onInputMinuteChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = onInputChange(event, () => minuteInputRef.current?.blur(), placeholder)

        value === undefined ? clearTime() : updateTime({ minute: value })
    }

    const onTimeSelect = (hour: number, minute: number) => {
        updateTime({ hour, minute })
    }

    const createKeyDownHandler = (type: 'hour' | 'minute') => (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace') {
            clearTime()
            event.preventDefault()
            return
        }

        if (event.key === 'Enter') {
            ;(event.target as HTMLInputElement).blur()
            return
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            const delta = event.key === 'ArrowDown' ? -1 : 1
            updateTime({ [type]: (timeValue?.get(type) || 0) + delta })
            return
        }
    }

    const onHourKeyDown = createKeyDownHandler('hour')
    const onMinuteKeyDown = createKeyDownHandler('minute')

    const handleInputFocus: FocusEventHandler<HTMLInputElement> = event => event.target.select()
    const handleClick: MouseEventHandler<HTMLElement> = event => {
        event.stopPropagation()
    }
    const handleInputClick: MouseEventHandler<HTMLInputElement> = event => {
        handleClick(event)
        ;(event.target as HTMLInputElement).select()
    }

    const hour = timeValue?.hour() ?? undefined
    const minute = timeValue?.minute() ?? undefined
    const hourDisplay = hour !== undefined ? prefixValue(hour) : placeholder
    const minuteDisplay = minute !== undefined ? prefixValue(minute) : placeholder
    const isEmpty = hour === undefined && minute === undefined

    return (
        <AstraInputWrapper
            id={id}
            onClick={() => hourInputRef.current?.select()}
            size={size}
            disabled={disabled}
            readOnly={readOnly}
            label={label}
            {...props}
            labelHtmlFor={`${label ?? placeholder}-input`}
            className={astraTwMerge('divide-x divide-strong pr-0', disabled ? 'divide-disabled' : '', sizeStyles[size])}
        >
            <div className="flex items-center gap-0.5">
                <input
                    onChange={onInputHourChange}
                    disabled={disabled}
                    readOnly={readOnly}
                    value={hourDisplay}
                    name={`${label ?? placeholder}-hour-input`}
                    type="text"
                    aria-label={'Hour input'}
                    aria-describedby={props.helperText ? getHelperTextId(id) : undefined}
                    className={astraTwMerge(timeInputStyles, inputTypographyStyles[size], hour === undefined ? 'text-muted' : '')}
                    ref={hourInputRef}
                    id={`${label ?? placeholder}-hour-input`}
                    onFocus={handleInputFocus}
                    onClick={handleInputClick}
                    onKeyDown={onHourKeyDown}
                    data-test={`${dataTest}-hour`}
                />
                <AstraParagraph
                    weight="regular"
                    variant={typographyStyles[size]}
                    className={astraTwMerge('select-none', disabled ? 'text-disabled' : '', readOnly ? 'text-default' : '', isEmpty ? 'text-muted' : '')}
                >
                    :
                </AstraParagraph>
                <input
                    onChange={onInputMinuteChange}
                    disabled={disabled}
                    readOnly={readOnly}
                    value={minuteDisplay}
                    name={`${label ?? placeholder}-minute-input`}
                    type="text"
                    aria-label={'Minute input'}
                    aria-describedby={props.helperText ? getHelperTextId(id) : undefined}
                    className={astraTwMerge(timeInputStyles, inputTypographyStyles[size], minute === undefined ? 'text-start text-muted' : '')}
                    ref={minuteInputRef}
                    id={`${label ?? placeholder}-minute-input`}
                    onFocus={handleInputFocus}
                    onClick={handleInputClick}
                    onKeyDown={onMinuteKeyDown}
                    data-test={`${dataTest}-minute`}
                />
            </div>
            <div>
                <AstraDropdownMenu
                    placement={`${side}-end`}
                    disabled={disabled || readOnly}
                    dropdownContent={
                        <AstraTimePickerSelectionMenu
                            timeSelection={timeSelection}
                            hourSelected={hour}
                            minuteSelected={minute}
                            onTimeSelect={onTimeSelect}
                            minTime={minTime}
                            maxTime={maxTime}
                        />
                    }
                    onClick={handleClick}
                >
                    <AstraButton
                        size={size}
                        variant="ghost"
                        icon={<AstraIcon icon={IconClockHour5} />}
                        aria-label={`Åpne ${label ?? 'tidsvelger'}`}
                        onClick={handleClick}
                        isRounded={false}
                    />
                </AstraDropdownMenu>
            </div>
        </AstraInputWrapper>
    )
}
